import { useNotificationsContext } from '@components/Notifications/NotificationsContext';
import { Antd6Provider } from '@components/shared/Antd6Provider';
import { useSystemLanguage } from '@contexts/SystemLanguageContext';
import {
  EDIT_FIELD_MFE_MODULE,
  FIELDS_LIST_CHANGED_EVENT,
  SEASON_MGT_CROPZONES_CHANGED_EVENT,
} from '@core/constants/fieldsMfe';
import type { Organization } from '@core/models/organization';
import type { Property } from '@core/models/properties';
import type { EditFieldProps } from '@cw-elements/host-kit/fields';
import { loadRemoteModule } from '@cw-elements/mfe-setup/load';
import { ErrorBoundary } from '@datadog/browser-rum-react';
import { editFieldStore } from '@state/editField/editField.store';
import { buildEditFieldDeepActionUrl } from '@utils/farmSettings.utils';
import { Spin } from 'antd-v6';
import { Suspense, lazy, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const EditFieldRemote = lazy(() => loadRemoteModule<EditFieldProps>(EDIT_FIELD_MFE_MODULE));

const getEntryPointPrefix = () =>
  globalThis.location.href.includes(import.meta.env.VITE_APP_CW_EXPLORER_DOMAIN)
    ? 'Cropwise Explorer'
    : 'Cropwise Imagery';

const EditFieldMfeUnavailable = () => {
  useEffect(() => {
    const { editing, markMfeUnavailable } = editFieldStore.getState();
    markMfeUnavailable();

    if (editing) {
      window.location.href = buildEditFieldDeepActionUrl({
        organizationId: editing.organizationId,
        propertyId: editing.propertyId,
        fieldId: editing.fieldId,
        callbackUri: window.location.href,
      });
    }
  }, []);

  return null;
};

const EditFieldMfeLoading = () => {
  const { t } = useTranslation();

  return (
    <Antd6Provider>
      <Spin fullscreen size="large" tip={t('fields_area.edit_loading')} />
    </Antd6Provider>
  );
};

interface EditFieldMfeHostProps {
  organization: Organization;
  property: Property;
}

/**
 * Hosts the Fields MFE editor (fields/edit) for the current property. Opened through
 * useOpenEditField; field and season data are refreshed by useRefreshOnFieldsMfeEvents.
 */
export function EditFieldMfe({ organization, property }: Readonly<EditFieldMfeHostProps>) {
  const { t } = useTranslation();
  const [language] = useSystemLanguage();
  const { success } = useNotificationsContext();
  const editing = editFieldStore((state) => state.editing);
  const hasOpened = editFieldStore((state) => state.hasOpened);
  const isMfeUnavailable = editFieldStore((state) => state.isMfeUnavailable);
  const closeEditField = editFieldStore((state) => state.closeEditField);
  const hasChangesRef = useRef(false);

  const isOpen = editing?.propertyId === property.id;

  // The MFE's own success toast lives inside the drawer and is lost when it closes, so remember
  // whether anything changed and confirm it on close.
  useEffect(() => {
    if (!isOpen) return;

    hasChangesRef.current = false;
    const markChanged = (event: Event) => {
      if (
        (event as CustomEvent<{ propertyId?: string } | null>).detail?.propertyId === property.id
      ) {
        hasChangesRef.current = true;
      }
    };

    globalThis.addEventListener(FIELDS_LIST_CHANGED_EVENT, markChanged);
    globalThis.addEventListener(SEASON_MGT_CROPZONES_CHANGED_EVENT, markChanged);

    return () => {
      globalThis.removeEventListener(FIELDS_LIST_CHANGED_EVENT, markChanged);
      globalThis.removeEventListener(SEASON_MGT_CROPZONES_CHANGED_EVENT, markChanged);
    };
  }, [isOpen, property.id]);

  // Stable on purpose: the MFE loader registers function props on window, so a new identity on
  // every render would re-register it and re-render the remote.
  const handleClose = useCallback(() => {
    if (hasChangesRef.current) {
      hasChangesRef.current = false;
      success({
        title: t('fields_area.edit_saved.title'),
        description: t('fields_area.edit_saved.description'),
      });
    }

    closeEditField();
  }, [closeEditField, success, t]);

  // Leaving the property (switching farms or unmounting) closes the editor.
  useEffect(() => closeEditField, [closeEditField, property.id]);

  if (!hasOpened || isMfeUnavailable) return null;

  return (
    <ErrorBoundary fallback={EditFieldMfeUnavailable}>
      <Suspense fallback={isOpen ? <EditFieldMfeLoading /> : null}>
        <EditFieldRemote
          orgId={organization.id}
          propertyId={property.id}
          fieldId={editing?.fieldId ?? ''}
          userLanguage={language}
          entryPoint={`${getEntryPointPrefix()} - ${editing?.entryPoint ?? 'Field List'}`}
          isOpen={isOpen}
          onClose={handleClose}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
